import {
  HtmlEditor,
  HeroEditor,
  PosterEditor,
  PostListEditor,
  CardListEditor,
  BlackBoxEditor,
  HeadlineEditor,
  PhotoWallEditor,
  ContactListEditor,
  RoleListEditor,
  RecentBlogPostsEditor,
  ResponsiveVideoEditor,
  FilteredCardListEditor
} from 'Dashboard/Editors';

import { uid, promptForValidValue } from 'Core/Utils/Utils';
import { isValidSlug } from 'Core/Utils/Validation';

export const CONTENT_TYPES = [
  {
    id: 'hero',
    name: 'Hero',
    editor: HeroEditor,
    description: 'A page header featuring a photo of your choice and optional buttons.'
  },
  {
    id: 'headline',
    name: 'Headline',
    editor: HeadlineEditor,
    description: `A classy section title.`
  },
  {
    id: 'blackbox',
    name: 'Black Box',
    editor: BlackBoxEditor,
    description: `Custom HTML inside a "black box" style section.`
  },
  {
    id: 'poster',
    name: 'Poster Image',
    editor: PosterEditor,
    description: `An image to show at full column width (nice 'n big).`
  },
  {
    id: 'html',
    name: 'HTML Block',
    editor: HtmlEditor,
    description: 'A block of custom HTML.'
  },
  {
    id: 'responsivevideo',
    name: 'Video Block',
    editor: ResponsiveVideoEditor,
    description: `A youtube video that automatically fills the column.`
  },
  {
    id: 'cardlist',
    name: 'Card List',
    editor: CardListEditor,
    description: `A list of linkable "cards", which can contain a title, an image (photo or "logo" style), and/or custom HTML.`
  },
  {
    id: 'filteredcardlist',
    name: 'Filterable Cards',
    editor: FilteredCardListEditor,
    description: `A "card list" wherein each card can be "tagged", and which shows filters to include only certain tags`
  },
  {
    id: 'postlist',
    name: 'Blog Post List',
    editor: PostListEditor,
    description: `A list of all a given blog's posts. Use the blog editor to create posts.`
  },
  {
    id: 'recentblogposts',
    name: 'Recent Posts',
    editor: RecentBlogPostsEditor,
    description: `A truncated list of recent blog posts from a given blog.`
  },
  {
    id: 'contactlist',
    name: 'Contact List',
    editor: ContactListEditor,
    description: `A list of "contacts", each including a name, address, phone, and lableable email and link fields.`
  },
  {
    id: 'photowall',
    name: 'Photo Wall',
    editor: PhotoWallEditor,
    description: `A gallery of photos or other images. Multiples of 4 work best.`
  },
  {
    id: 'rolelist',
    name: 'Role List',
    editor: RoleListEditor,
    description: `A list of roles, like career or internship availabilities. Optionally grouped and filterable.`
  }
].map(contentType => ({
  ...contentType,
  image: `/assets/rlx/content-types/${contentType.id}.png`
}));

export function getEmptyPage () {
  return {
    name: '',
    url: '',
    id: uid(),
    content: [],
    shy: false,
    external: false,
    hidden: false
  };
}

export function askForBlogId (initialId = uid()) {
  return promptForValidValue('Please enter a URL-safe ID for this blog. Please use only letters, numbers, and/or hyphens.', initialId, isValidSlug);
}

export function getEmptyBlog () {
  const id = askForBlogId();
  if (!id) throw new TypeError(`ContentUtils.getEmptyBlog: didn't receive a valid ID input. (${id})`);
  return {
    id,
    posts: []
  };
}

export function getContentType (type) {
  if (typeof type !== 'string') throw new Error(`ContentUtils.getContentType: 'type' is not a string. (${type})`);
  const contentType = CONTENT_TYPES.find(({ id }) => id.toLowerCase() === type.toLowerCase());
  if (!contentType) throw new Error(`ContentUtils.getContentType: type "${type}" not found.`);
  return contentType;
}

export function getContentTypeIcon (type) {
  const contentType = getContentType(type);
  return contentType.image;
}

export function getContentForm (type) {
  const contentType = getContentType(type);
  return contentType.editor;
};

export function getInitialContentShape (type) {
  const FormComponent = getContentForm(type);
  return FormComponent.getInitialShape();
};



// WEBPACK FOOTER //
// ./client/Core/Utils/ContentUtils.js